import { DocumentData } from 'features/baseLens'
import { DocumentType } from 'features/files/types'
import { sanitizeFilenameForRolodex } from 'features/files/utils/fileUploadUtils'
import {
  RolodexEntryDataType,
  RolodexEntryPutJson,
  RolodexEntryType,
} from 'types'
import { v4 as uuidv4 } from 'uuid'

export interface DocumentUploadOptions {
  file: File
  tags: Record<string, string>
  organizationId: string
  metadata: Partial<DocumentData>
  rolodexPut: (entries: RolodexEntryPutJson[]) => Promise<unknown>
  rolodexUploadFile: (
    entry_id: string,
    file: Blob,
    toPath: string
  ) => Promise<unknown>
  refreshDocuments: () => void
  uploadFileToSage: ({
    file,
    organization,
    metadata,
  }: {
    file: File
    organization: string
    metadata: Record<string, string>
  }) => Promise<{
    success: boolean
    document?: DocumentData
    message?: string
  }>
  onStatusUpdate?: (status: string) => void
}

export class DocumentService {
  private geckoSageUrl: string

  constructor(geckoSageUrl: string) {
    this.geckoSageUrl = geckoSageUrl
  }

  /**
   * Upload a document and create its metadata
   */
  async uploadDocument({
    file,
    tags,
    organizationId,
    metadata,
    rolodexPut,
    rolodexUploadFile,
    refreshDocuments,
    uploadFileToSage,
    onStatusUpdate,
  }: DocumentUploadOptions): Promise<{
    success: boolean
    document?: DocumentData
    message?: string
  }> {
    try {
      onStatusUpdate?.('Preparing document metadata...')

      // Generate a unique ID for the document
      const documentId = metadata.id || `DOC-${uuidv4().substring(0, 8)}`

      // Sanitize the filename for safe storage
      const sanitizedFilename = sanitizeFilenameForRolodex(file.name)

      // Create document metadata with sanitized filename
      const newDocument: DocumentData = {
        id: documentId,
        title: metadata.title || sanitizedFilename,
        filename: sanitizedFilename,
        type: metadata.type || this.getFileType(file),
        uploadDate: new Date().toISOString().split('T')[0],
        size: this.formatFileSize(file.size),
        description: metadata.description || '',
      }

      onStatusUpdate?.('Saving document to database...')

      // Update the document with the file URL and create the entry
      await rolodexPut([
        {
          id: documentId,
          type: RolodexEntryType.SYSTEM_DOCUMENT,
          data: newDocument as unknown as RolodexEntryDataType,
          tags,
          index: {},
        },
      ])

      onStatusUpdate?.('Uploading file to storage...')
      await rolodexUploadFile(documentId, file, sanitizedFilename)

      onStatusUpdate?.('Indexing document for AI search...')
      await uploadFileToSage({
        file,
        organization: organizationId,
        metadata: {
          ...metadata,
          ...tags,
        },
      })

      onStatusUpdate?.('Finalizing upload...')
      // Refresh documents
      refreshDocuments()

      return {
        success: true,
        document: newDocument,
      }
    } catch (error) {
      console.error('Error uploading document:', error)
      return {
        success: false,
        message: `Error uploading document: ${String(error)}`,
      }
    }
  }

  /**
   * Helper method to determine file type
   */
  private getFileType(file: File): DocumentType {
    const extension = file.name.split('.').pop()?.toLowerCase() || ''

    const typeMap: Record<string, DocumentType> = {
      pdf: DocumentType.PDF,
      doc: DocumentType.WORD,
      docx: DocumentType.WORD,
      xls: DocumentType.EXCEL,
      xlsx: DocumentType.EXCEL,
      ppt: DocumentType.POWERPOINT,
      pptx: DocumentType.POWERPOINT,
      txt: DocumentType.TEXT,
      csv: DocumentType.CSV,
      jpg: DocumentType.IMAGE,
      jpeg: DocumentType.IMAGE,
      png: DocumentType.IMAGE,
      gif: DocumentType.IMAGE,
    }

    return typeMap[extension] || DocumentType.OTHER
  }

  /**
   * Helper method to format file size
   */
  private formatFileSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`
    } else if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`
    } else {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }
  }

  /**
   * Delete a document
   */
  async deleteDocument(
    documentId: string,
    rolodexDelete: (id: string, deleteFiles: boolean) => Promise<unknown>
  ): Promise<{ success: boolean; message?: string }> {
    try {
      await rolodexDelete(documentId, true)

      return {
        success: true,
      }
    } catch (error) {
      console.error('Error deleting document:', error)
      return {
        success: false,
        message: `Error deleting document: ${String(error)}`,
      }
    }
  }
}
